import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { Child, ChildGoal } from "../types/child";
import { fireDataRefresh } from "../utils/events";
import ConfirmationModal from "../components/ConfirmationModal";

interface ChildManagementProps {
  user: any;
  darkMode: boolean;
  childrenCount: number;
  onChildrenUpdated: () => void;
}

const ChildManagement: React.FC<ChildManagementProps> = ({
  user,
  darkMode,
  childrenCount,
  onChildrenUpdated,
}) => {
  const [children, setChildren] = useState<Child[]>([]);
  const [childGoals, setChildGoals] = useState<{ [key: string]: ChildGoal }>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [newChildName, setNewChildName] = useState("");
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [editingGoals, setEditingGoals] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [originalChildGoals, setOriginalChildGoals] = useState<{
    [key: string]: {
      monday_goal_minutes: number | null;
      tuesday_goal_minutes: number | null;
      wednesday_goal_minutes: number | null;
      thursday_goal_minutes: number | null;
      friday_goal_minutes: number | null;
      saturday_goal_minutes: number | null;
      sunday_goal_minutes: number | null;
    };
  }>({});
  const [editingChildGoalValues, setEditingChildGoalValues] = useState<{
    [key: string]: {
      monday_goal_minutes: number | null;
      tuesday_goal_minutes: number | null;
      wednesday_goal_minutes: number | null;
      thursday_goal_minutes: number | null;
      friday_goal_minutes: number | null;
      saturday_goal_minutes: number | null;
      sunday_goal_minutes: number | null;
    };
  }>({});
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [childToDelete, setChildToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Helper function to capitalize names properly
  const capitalizeName = (name: string): string => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    if (!user || !user.id) return;
    fetchChildren();
  }, [user]);

  const fetchChildren = async () => {
    if (!user || !user.id) return;
    try {
      setLoading(true);

      // Fetch children
      const { data: childrenData, error: childrenError } = await supabase
        .from("children")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (childrenError) throw childrenError;

      setChildren(childrenData || []);

      // Fetch goals for each child
      if (childrenData && childrenData.length > 0) {
        const childIds = childrenData.map((child) => child.id);
        const { data: goalsData, error: goalsError } = await supabase
          .from("child_goals")
          .select("*")
          .in("child_id", childIds);

        if (goalsError) throw goalsError;

        const goalsMap: { [key: string]: ChildGoal } = {};
        goalsData?.forEach((goal) => {
          goalsMap[goal.child_id] = goal;
        });

        setChildGoals(goalsMap);
      }
    } catch (error) {
      console.error("Error fetching children:", error);
      setError("Failed to load children");
    } finally {
      setLoading(false);
    }
  };

  const addChild = async () => {
    if (!newChildName.trim()) {
      setError("Please enter a child name");
      return;
    }

    // Capitalize the name properly
    const capitalizedName = capitalizeName(newChildName);

    // Validate child name: letters, spaces, hyphens, apostrophes only (2-100 chars)
    const namePattern = /^[A-Za-z\s\-']{2,100}$/;
    if (!namePattern.test(capitalizedName)) {
      setError(
        "Child name must be 2-100 characters and contain only letters, spaces, hyphens, or apostrophes.",
      );
      return;
    }

    try {
      setError("");

      // Check if child name already exists
      const existingChild = children.find(
        (child) => child.name.toLowerCase() === capitalizedName.toLowerCase(),
      );

      if (existingChild) {
        setError("A child with this name already exists");
        return;
      }

      // Insert child
      const { data: newChild, error: childError } = await supabase
        .from("children")
        .insert({
          user_id: user.id,
          name: capitalizedName,
        })
        .select()
        .single();

      if (childError) throw childError;

      // Insert default goals for the child (match app logic: set all 7 day-specific goals)
      const { error: goalsError } = await supabase.from("child_goals").insert({
        child_id: newChild.id,
        // Set all 7 days to 240 minutes (4 hours) - weekly_goal_minutes auto-calculated by DB trigger
        monday_goal_minutes: 240,
        tuesday_goal_minutes: 240,
        wednesday_goal_minutes: 240,
        thursday_goal_minutes: 240,
        friday_goal_minutes: 240,
        saturday_goal_minutes: 240,
        sunday_goal_minutes: 240,
        // weekly_goal_minutes is auto-calculated by DB trigger
      });

      if (goalsError) throw goalsError;

      // Snapshot default child goals into history
      await supabase.from("goal_history").upsert({
        user_id: user.id,
        child_id: newChild.id,
        effective_from: new Date().toISOString().split("T")[0],
        sunday_goal_minutes: 240,
        monday_goal_minutes: 240,
        tuesday_goal_minutes: 240,
        wednesday_goal_minutes: 240,
        thursday_goal_minutes: 240,
        friday_goal_minutes: 240,
        saturday_goal_minutes: 240,
      }, { onConflict: "user_id,child_id,effective_from" });

      // If this is the first child, update user's treatment_type from 'self' to 'child'
      if (children.length === 0) {
        const { error: userUpdateError } = await supabase
          .from("user_data")
          .update({ treatment_type: "child" })
          .eq("user_id", user.id);

        if (userUpdateError) {
          console.error("Error updating user treatment_type:", userUpdateError);
          // Don't throw error here as the child was added successfully
        }
      }

      setChildren([...children, newChild]);
      setNewChildName("");
      onChildrenUpdated();
      fireDataRefresh({ reason: "child_added" });
    } catch (error) {
      console.error("Error adding child:", error);
      setError("Failed to add child");
    }
  };

  const updateChild = async (child: Child, newName: string) => {
    if (!newName.trim()) {
      setError("Please enter a child name");
      return;
    }

    // Capitalize the name properly
    const capitalizedName = capitalizeName(newName);

    const namePattern = /^[A-Za-z\s\-']{2,100}$/;
    if (!namePattern.test(capitalizedName)) {
      setError(
        "Child name must be 2-100 characters and contain only letters, spaces, hyphens, or apostrophes.",
      );
      return;
    }

    try {
      setError("");

      // Check if name already exists (excluding current child)
      const existingChild = children.find(
        (c) =>
          c.id !== child.id &&
          c.name.toLowerCase() === capitalizedName.toLowerCase(),
      );

      if (existingChild) {
        setError("A child with this name already exists");
        return;
      }

      const { error } = await supabase
        .from("children")
        .update({ name: capitalizedName })
        .eq("id", child.id);

      if (error) throw error;

      setChildren(
        children.map((c) =>
          c.id === child.id ? { ...c, name: capitalizedName } : c,
        ),
      );
      setEditingChild(null);
      onChildrenUpdated();
      fireDataRefresh({ reason: "child_updated" });
    } catch (error) {
      console.error("Error updating child:", error);
      setError("Failed to update child");
    }
  };

  const handleDeleteClick = (childId: string) => {
    const child = children.find((c) => c.id === childId);
    const name = child?.name || "this child";
    setChildToDelete({ id: childId, name });
    setShowDeleteModal(true);
  };

  const deleteChild = async () => {
    if (!childToDelete) return;

    try {
      setError("");

      const { error } = await supabase
        .from("children")
        .delete()
        .eq("id", childToDelete.id);

      if (error) throw error;

      const updatedChildren = children.filter((c) => c.id !== childToDelete.id);
      setChildren(updatedChildren);
      const newGoals = { ...childGoals };
      delete newGoals[childToDelete.id];
      setChildGoals(newGoals);

      // If this was the last child, update user's treatment_type back to 'self'
      if (updatedChildren.length === 0) {
        const { error: userUpdateError } = await supabase
          .from("user_data")
          .update({ treatment_type: "self" })
          .eq("user_id", user.id);

        if (userUpdateError) {
          console.error("Error updating user treatment_type:", userUpdateError);
          // Don't throw error here as the child was deleted successfully
        }
      }

      onChildrenUpdated();
      fireDataRefresh({ reason: "child_deleted" });
      setShowDeleteModal(false);
      setChildToDelete(null);
    } catch (error) {
      console.error("Error deleting child:", error);
      setError("Failed to delete child");
      setShowDeleteModal(false);
      setChildToDelete(null);
    }
  };

  const handleSaveGoals = async (childId: string) => {
    const currentValues = editingChildGoalValues[childId];
    if (!currentValues) {
      setError("Could not find goal values");
      return;
    }

    // Validate each day's goal (30-480 minutes or 0/null for rest day)
    const dayKeys = [
      "monday_goal_minutes",
      "tuesday_goal_minutes",
      "wednesday_goal_minutes",
      "thursday_goal_minutes",
      "friday_goal_minutes",
      "saturday_goal_minutes",
      "sunday_goal_minutes",
    ] as const;

    for (const key of dayKeys) {
      const value = currentValues[key];
      if (value === null || value === 0) continue; // rest day is valid
      if (value < 30) {
        setError("Daily goals must be 30+ minutes (or 0 for rest day).");
        return;
      }
      if (value > 480) {
        setError("Daily goals must be 480 minutes (8 hours) or less.");
        return;
      }
    }

    try {
      setError("");

      // Check if child goals exist first (match app logic)
      const { data: existing } = await supabase
        .from("child_goals")
        .select("child_id")
        .eq("child_id", childId)
        .maybeSingle();

      let err;
      if (existing) {
        // Update existing record - do NOT set daily_goal_minutes
        const { error } = await supabase
          .from("child_goals")
          .update({
            monday_goal_minutes:
              currentValues.monday_goal_minutes === 0
                ? null
                : currentValues.monday_goal_minutes,
            tuesday_goal_minutes:
              currentValues.tuesday_goal_minutes === 0
                ? null
                : currentValues.tuesday_goal_minutes,
            wednesday_goal_minutes:
              currentValues.wednesday_goal_minutes === 0
                ? null
                : currentValues.wednesday_goal_minutes,
            thursday_goal_minutes:
              currentValues.thursday_goal_minutes === 0
                ? null
                : currentValues.thursday_goal_minutes,
            friday_goal_minutes:
              currentValues.friday_goal_minutes === 0
                ? null
                : currentValues.friday_goal_minutes,
            saturday_goal_minutes:
              currentValues.saturday_goal_minutes === 0
                ? null
                : currentValues.saturday_goal_minutes,
            sunday_goal_minutes:
              currentValues.sunday_goal_minutes === 0
                ? null
                : currentValues.sunday_goal_minutes,
            // weekly_goal_minutes is auto-calculated by DB trigger
          })
          .eq("child_id", childId);
        err = error;
      } else {
        // Insert new record
        const { error } = await supabase.from("child_goals").insert({
          child_id: childId,
          monday_goal_minutes:
            currentValues.monday_goal_minutes === 0
              ? null
              : currentValues.monday_goal_minutes,
          tuesday_goal_minutes:
            currentValues.tuesday_goal_minutes === 0
              ? null
              : currentValues.tuesday_goal_minutes,
          wednesday_goal_minutes:
            currentValues.wednesday_goal_minutes === 0
              ? null
              : currentValues.wednesday_goal_minutes,
          thursday_goal_minutes:
            currentValues.thursday_goal_minutes === 0
              ? null
              : currentValues.thursday_goal_minutes,
          friday_goal_minutes:
            currentValues.friday_goal_minutes === 0
              ? null
              : currentValues.friday_goal_minutes,
          saturday_goal_minutes:
            currentValues.saturday_goal_minutes === 0
              ? null
              : currentValues.saturday_goal_minutes,
          sunday_goal_minutes:
            currentValues.sunday_goal_minutes === 0
              ? null
              : currentValues.sunday_goal_minutes,
          // weekly_goal_minutes is auto-calculated by DB trigger
        });
        err = error;
      }

      if (err) throw err;

      // Snapshot updated child goals into history
      await supabase.from("goal_history").upsert({
        user_id: user.id,
        child_id: childId,
        effective_from: new Date().toISOString().split("T")[0],
        sunday_goal_minutes: currentValues.sunday_goal_minutes === 0 ? null : currentValues.sunday_goal_minutes,
        monday_goal_minutes: currentValues.monday_goal_minutes === 0 ? null : currentValues.monday_goal_minutes,
        tuesday_goal_minutes: currentValues.tuesday_goal_minutes === 0 ? null : currentValues.tuesday_goal_minutes,
        wednesday_goal_minutes: currentValues.wednesday_goal_minutes === 0 ? null : currentValues.wednesday_goal_minutes,
        thursday_goal_minutes: currentValues.thursday_goal_minutes === 0 ? null : currentValues.thursday_goal_minutes,
        friday_goal_minutes: currentValues.friday_goal_minutes === 0 ? null : currentValues.friday_goal_minutes,
        saturday_goal_minutes: currentValues.saturday_goal_minutes === 0 ? null : currentValues.saturday_goal_minutes,
      }, { onConflict: "user_id,child_id,effective_from" });

      // Re-fetch goals to get updated data
      await fetchChildren();

      // Update original goals to match current form values
      setOriginalChildGoals({
        ...originalChildGoals,
        [childId]: { ...currentValues },
      });

      setEditingGoals({ ...editingGoals, [childId]: false });
      onChildrenUpdated();
      fireDataRefresh({ reason: "child_goals_updated" });
    } catch (error: any) {
      console.error("Error updating child goals:", error);
      setError("Failed to update child goals");
    }
  };

  const handleEditGoals = (childId: string) => {
    // Store original values when editing starts
    const currentGoals = childGoals[childId];
    const originalValues = {
      monday_goal_minutes: (currentGoals as any)?.monday_goal_minutes ?? null,
      tuesday_goal_minutes: (currentGoals as any)?.tuesday_goal_minutes ?? null,
      wednesday_goal_minutes:
        (currentGoals as any)?.wednesday_goal_minutes ?? null,
      thursday_goal_minutes:
        (currentGoals as any)?.thursday_goal_minutes ?? null,
      friday_goal_minutes: (currentGoals as any)?.friday_goal_minutes ?? null,
      saturday_goal_minutes:
        (currentGoals as any)?.saturday_goal_minutes ?? null,
      sunday_goal_minutes: (currentGoals as any)?.sunday_goal_minutes ?? null,
    };

    setOriginalChildGoals({
      ...originalChildGoals,
      [childId]: originalValues,
    });

    // Initialize editing values with current values (display 0 for null)
    setEditingChildGoalValues({
      ...editingChildGoalValues,
      [childId]: {
        monday_goal_minutes: originalValues.monday_goal_minutes ?? 0,
        tuesday_goal_minutes: originalValues.tuesday_goal_minutes ?? 0,
        wednesday_goal_minutes: originalValues.wednesday_goal_minutes ?? 0,
        thursday_goal_minutes: originalValues.thursday_goal_minutes ?? 0,
        friday_goal_minutes: originalValues.friday_goal_minutes ?? 0,
        saturday_goal_minutes: originalValues.saturday_goal_minutes ?? 0,
        sunday_goal_minutes: originalValues.sunday_goal_minutes ?? 0,
      },
    });

    setEditingGoals({ ...editingGoals, [childId]: true });
  };

  const handleCancelEditGoals = (childId: string) => {
    setEditingGoals({ ...editingGoals, [childId]: false });
    // Clean up tracking state
    const { [childId]: _, ...restOriginal } = originalChildGoals;
    const { [childId]: __, ...restEditing } = editingChildGoalValues;
    setOriginalChildGoals(restOriginal);
    setEditingChildGoalValues(restEditing);
  };

  const handleGoalValueChange = (
    childId: string,
    dayKey:
      | "monday_goal_minutes"
      | "tuesday_goal_minutes"
      | "wednesday_goal_minutes"
      | "thursday_goal_minutes"
      | "friday_goal_minutes"
      | "saturday_goal_minutes"
      | "sunday_goal_minutes",
    value: number,
  ) => {
    setEditingChildGoalValues({
      ...editingChildGoalValues,
      [childId]: {
        ...editingChildGoalValues[childId],
        [dayKey]: value,
      },
    });
  };

  const hasChildGoalsChanged = (childId: string): boolean => {
    const original = originalChildGoals[childId];
    const current = editingChildGoalValues[childId];

    if (!original || !current) return false;

    const dayKeys = [
      "monday_goal_minutes",
      "tuesday_goal_minutes",
      "wednesday_goal_minutes",
      "thursday_goal_minutes",
      "friday_goal_minutes",
      "saturday_goal_minutes",
      "sunday_goal_minutes",
    ] as const;

    // Compare each day, treating 0 and null as equivalent
    for (const key of dayKeys) {
      const currentVal = current[key] === 0 ? null : current[key];
      const originalVal = original[key];
      if (currentVal !== originalVal) {
        return true;
      }
    }

    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-dark-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2
          className={`text-2xl font-bold mb-2 ${
            darkMode ? "text-white" : "text-brand-dark-blue"
          }`}
        >
          Children Management
        </h2>
        <p className={`${darkMode ? "text-gray-300" : "text-gray-600"} mb-4`}>
          Add and manage your children's information and goals.
        </p>

        {/* Message about adding children only if you have multiple children */}
        <div
          className={`p-4 rounded-lg border-2 border-dashed ${
            darkMode
              ? "border-gray-600 bg-gray-800/50"
              : "border-gray-300 bg-gray-50"
          } mb-6`}
        >
          <p
            className={`text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            <strong>Note:</strong>{" "}
            {childrenCount === 0
              ? "Only add children here if you have multiple children to track. If you have only one child, you can manage their goals directly in the dashboard."
              : "You can manage multiple children's information and goals here."}
          </p>
        </div>
      </div>

      {/* Add New Child */}
      <div
        className={`p-4 rounded-lg border ${
          darkMode
            ? "bg-gray-800 border-gray-600 shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
            : "bg-white border-gray-200 shadow-lg"
        }`}
      >
        <h4
          className={`font-medium mb-3 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Add New Child
        </h4>
        <div className="flex gap-3">
          <input
            type="text"
            maxLength={100}
            value={newChildName}
            onChange={(e) => setNewChildName(e.target.value)}
            onBlur={(e) => setNewChildName(capitalizeName(e.target.value))}
            placeholder="Child's name"
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent ${
              darkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "border-gray-300"
            }`}
          />
          <button
            onClick={addChild}
            className="px-4 py-2 bg-brand-dark-blue text-white rounded-lg hover:bg-brand-cyan shadow-md hover:shadow-lg transition-all duration-200 font-medium"
          >
            Add Child
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className={`p-4 rounded-lg text-sm font-medium shadow-lg ${
            darkMode
              ? "bg-red-900/80 text-red-300 border border-red-700 shadow-red-900/20"
              : "bg-red-50 text-red-800 border border-red-200 shadow-red-200/20"
          }`}
        >
          {error}
        </div>
      )}

      {/* Children List */}
      <div className="space-y-4">
        {children.map((child) => (
          <div
            key={child.id}
            className={`p-4 rounded-lg border ${
              darkMode
                ? "bg-gray-800 border-gray-600 shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                : "bg-white border-gray-200 shadow-lg"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {editingChild?.id === child.id ? (
                  <input
                    type="text"
                    maxLength={100}
                    defaultValue={child.name}
                    onBlur={(e) => updateChild(child, e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        updateChild(child, e.currentTarget.value);
                      }
                    }}
                    className={`px-2 py-1 border rounded ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-gray-300"
                    }`}
                    autoFocus
                  />
                ) : (
                  <h4
                    className={`font-medium ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {child.name}
                  </h4>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setEditingChild(
                      editingChild?.id === child.id ? null : child,
                    )
                  }
                  className={`text-sm px-3 py-1 rounded transition-all duration-200 ${
                    editingChild?.id === child.id
                      ? "bg-gray-500 text-white hover:bg-gray-600"
                      : darkMode
                        ? "bg-brand-dark-blue text-white hover:bg-brand-cyan shadow-md hover:shadow-lg"
                        : "bg-brand-dark-blue text-white hover:bg-brand-cyan shadow-md hover:shadow-lg"
                  }`}
                >
                  {editingChild?.id === child.id ? "Cancel" : "Edit Name"}
                </button>
                <button
                  onClick={() => handleDeleteClick(child.id)}
                  className="text-sm px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Child Goals */}
            <div className="mt-4">
              <h5
                className={`text-sm font-medium mb-3 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Goals
              </h5>
              {editingGoals[child.id] ? (
                <div className="space-y-4">
                  <div
                    className={`text-sm mb-3 ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Set goal for each day. Enter <b>0</b> for a rest day. Weekly
                    goal is automatically calculated.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(
                      [
                        ["Monday", "monday_goal_minutes"],
                        ["Tuesday", "tuesday_goal_minutes"],
                        ["Wednesday", "wednesday_goal_minutes"],
                        ["Thursday", "thursday_goal_minutes"],
                        ["Friday", "friday_goal_minutes"],
                        ["Saturday", "saturday_goal_minutes"],
                        ["Sunday", "sunday_goal_minutes"],
                      ] as const
                    ).map(([label, key]) => {
                      const value =
                        editingChildGoalValues[child.id]?.[key] ?? 0;
                      const isRestDay = value === 0 || value === null;

                      return (
                        <div key={key}>
                          <label
                            className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            <span>{label}</span>
                            {isRestDay && (
                              <span
                                className="text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded"
                                title="Rest Day"
                              >
                                🛌 Rest
                              </span>
                            )}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={value}
                              onChange={(e) =>
                                handleGoalValueChange(
                                  child.id,
                                  key,
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                                isRestDay
                                  ? darkMode
                                    ? "bg-green-900/20 border-green-600 text-green-200"
                                    : "bg-green-50 border-green-300 text-green-800"
                                  : darkMode
                                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                    : "border-gray-300"
                              }`}
                              placeholder="240 (4 hours)"
                              min="0"
                              max="480"
                            />
                            {!isRestDay && value > 0 && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                {Math.floor(value / 60)}h {value % 60}m
                              </div>
                            )}
                          </div>
                          {isRestDay && (
                            <p
                              className={`text-xs mt-1 ${
                                darkMode ? "text-green-300" : "text-green-600"
                              }`}
                            >
                              Rest day - no goal
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSaveGoals(child.id)}
                      disabled={!hasChildGoalsChanged(child.id)}
                      className="flex-1 bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      Save Goals
                    </button>
                    <button
                      onClick={() => handleCancelEditGoals(child.id)}
                      className={`flex-1 px-4 py-2 border rounded-lg transition-all duration-200 ${
                        darkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500"
                          : "border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Weekly goal:{" "}
                    {childGoals[child.id]?.weekly_goal_minutes || 1680} minutes
                    (auto-calculated)
                  </div>
                  <button
                    onClick={() => handleEditGoals(child.id)}
                    className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02]"
                  >
                    Edit Goals
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {children.length === 0 && (
        <div
          className={`text-center py-8 ${
            darkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          No children added yet. Add your first child above.
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {childToDelete && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setChildToDelete(null);
          }}
          onConfirm={deleteChild}
          title="Delete Child"
          message={`This will permanently delete ${childToDelete.name}, including all logs and goals. This action cannot be undone.`}
          confirmText={`DELETE ${childToDelete.name.toUpperCase()}`}
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          danger={true}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default ChildManagement;
